import { createTheme } from "@mui/material";

const baseTheme = createTheme({
  typography: {
    fontFamily: '"Poppins", sans-serif',
    body1: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 300,
      fontSize: "16px",
      letterSpacing: "0.5px",
    },
    body2: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 300,
      fontSize: "14px",
      letterSpacing: "0.5px",
    },
    caption: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 300,
      fontSize: "12px",
      letterSpacing: "1px",
    },
    h1: {
      fontFamily: '"Tahoma", sans-serif',
      fontWeight: 700,
      fontSize: "60px",
      lineHeight: "5rem",
    },
    h2: {
      fontFamily: '"Tahoma", sans-serif',
      fontWeight: 700,
      fontSize: "35px",
      lineHeight: "3rem",
      letterSpacing: "1.5px",
      textAlign: "left",
    },
    h3: {
      fontFamily: '"Tahoma", sans-serif',
      fontWeight: 700,
      fontSize: "32px",
      lineHeight: "2.5rem",
    },
    h4: {
      fontFamily: '"Tahoma", sans-serif',
      fontWeight: 700,
      fontSize: "30px",
      lineHeight: "2.25rem",
    },
    h5: { fontFamily: '"Tahoma", sans-serif', fontWeight: 700 }, // 24px
    subtitle1: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
      fontSize: "24px",
      lineHeight: "1.75rem",
    },
    subtitle2: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
      fontSize: "20px",
      lineHeight: "2rem",
    },
    subtitle3: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
      fontSize: "18px",
      lineHeight: "2rem",
    },
  },
  palette: {
    primary: {
      black: "#000000",
      black80: "rgba(0, 0, 0, 0.8)",
      black60: "rgba(0, 0, 0, 0.6)",
      black40: "rgba(0, 0, 0, 0.4)",
      white: "#fff",
      main: "#151A40",
      main80: "rgba(21, 26, 64, 0.8)",
      main60: "rgba(21, 26, 64, 0.6)",
      main40: "rgba(21, 26, 64, 0.4)",
      mistGray: "#F4F5FA",
      brandMain: "#B9FF66",
      brandSecondary: "#8F7EF8",
      lightBackground: "#f8fafc", // Delete Later ‼️
      background: "#f1f5f9", // Delete Later ‼️
    },
    secondary: {
      main: "#0DC3B2",
      tiffanyBlue: "#0DC3B2",
      lawnGreen: "#AED135",
      berry: "#B8117E",
      chilliRed: "#F40058",
      sunriseOrange: "#EFA500",
      lightBlack: "rgba(0, 0, 0, 0.1)",
    },
    linearGradient: {
      gradientMain: "linear-gradient(270deg, #4E4282 45%, #000000 90%)",
      gradientSecondary: "linear-gradient(328deg, #8F7EF8 0%, #4E4C72 80%)",
      gradientTiffanyBlue: "linear-gradient(328deg, #0DC3B2 0%, #23726B 80%)",
      gradientChilliRed: "linear-gradient(328deg, #F40058 0%, #B11845 80%)",
      gradientBerry: "linear-gradient(328deg, #B8117E 0%, #73084D 80%)",
      gradientSunriseOrange: "linear-gradient(328deg, #EFA500 0%, #CC8E01 80%)",
      gradientLawnGreen: "linear-gradient(328deg, #AED135 0%, #62BB47 80%)",
    },
    gray: {
      lighter: "#fafafa", // Delete Later ‼️
      light: "#f5f5f5", // Delete Later ‼️
      medium: "#e0e0e0", // Delete Later ‼️
      dark: "#9e9e9e", // Delete Later ‼️
      medDark: "#757575", // Delete Later ‼️
      darker: "#494b4b", // Delete Later ‼️
      ultraDark: "#161616", // Delete Later ‼️
    },
  },
  customBackgrounds: {
    lightBackground: "rgba(255, 255, 255, 0.1)",
    mediumBackground: "rgba(255, 255, 255, 0.4)",
    darkBackground: "rgba(255, 255, 255, 0.7)",
  },
  customShadows: {
    light: "0px 0px 10px rgba(0, 0, 0, 0.1)", //this is medium
    medium: "0 2px 4px rgba(0,0,0,0.1)", //this is ligher  "0 1px 3px rgba(0,0,0,0.1)"
    larger: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
  borderRadius: {
    xsmall: "6px",
    small: "8px",
    medium: "10px",
    large: "16px",
    xl: "20px",
    xxl: "24px",
    xxxl: "32px",
    xxxxl: "40px",
    full: "100px",
    border50: "50%",
  },
});

const theme = createTheme(baseTheme, {
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        a: {
          textDecoration: "none",
          color: baseTheme.palette.secondary.tiffanyBlue,
          fontWeight: 500,
          cursor: "pointer",
          "&:hover": {
            textDecoration: "underline",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: baseTheme.palette.primary.main,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: baseTheme.palette.primary.main,
          },
          "&.Mui-focused": {
            borderColor: "transparent",
          },
          "&.MuiInputBase-root": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          "& .MuiTypography-root": {
            color: baseTheme.palette.primary.main,
          },
        },
      },
    },

    // Icon styles
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.primary.main,
          color: baseTheme.palette.primary.brandMain,
          boxShadow: baseTheme.shadows[3],
          "&:hover": {
            color: baseTheme.palette.primary.main,
            backgroundColor: baseTheme.palette.primary.brandMain,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "14px",
          maxWidth: "300px",
          whiteSpace: "pre-wrap",
        },
      },
    },

    // Paper / cards / container styles
    MuiPaper: {
      styleOverrides: {
        root: {
          marginTop: "6px",
        },
      },
      variants: [
        {
          props: { variant: "historyItem" },
          style: {
            padding: "12px",
            marginBottom: "8px",
            borderRadius: "4px",
            cursor: "pointer",
            transition: "background-color 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: baseTheme.palette.gray.light,
            },
            position: "relative",
          },
        },
      ],
    },

    // Form Styles
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.primary.main60,
          "&.Mui-focused": {
            color: baseTheme.palette.primary.brandSecondary,
          },
          "&.Mui-error": {
            color: baseTheme.palette.secondary.chilliRed,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.primary.mistGray,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: baseTheme.palette.primary.brandSecondary,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: baseTheme.palette.primary.brandSecondary,
            boxShadow: `4px 4px 12px ${baseTheme.palette.primary.brandSecondary}45`,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: baseTheme.palette.primary.brandSecondary,
            boxShadow: `4px 4px 16px ${baseTheme.palette.primary.brandSecondary}70`,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: baseTheme.palette.secondary.chilliRed,
            boxShadow: "none",
          },
          "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
            boxShadow: "none",
            borderColor: baseTheme.palette.secondary.chilliRed,
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: baseTheme.palette.primary.brandSecondary,
            boxShadow: "none",
          },
          "&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: baseTheme.palette.primary.brandSecondary,
            boxShadow: "none",
          },
        },
      },
    },

    // Table Styles
    MuiTableContainer: {
      styleOverrides: {
        root: {
          margin: "24px 0",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          textAlign: "left",
        },
        head: {
          backgroundColor: baseTheme.palette.gray.light, // delete after
          fontWeight: 600,
          fontSize: baseTheme.typography.body1.fontSize,
          letterSpacing: "0.5px",
          whiteSpace: "nowrap",
          py: "1rem",
        },
        body: {
          padding: "1rem",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&.MuiTableRow-hover:hover": {
            backgroundColor: baseTheme.palette.gray.lighter,
          },
        },
      },
    },

    // Drawer styles
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: baseTheme.palette.primary.white,
          boxShadow: baseTheme.customShadows.light,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
          "&.Mui-expanded": {
            margin: "0px",
          },
        },
      },
    },
  },
});

export default theme;
